import { bind, Component, h, tag } from "omi";

import { NWBadge } from "@/wc/components/atoms/nw-badge";
import { NWButton } from "@/wc/components/atoms/nw-button";
import { openLoginModal, openProModal } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { BackgroundLines } from "../../ui/backgroundLines";
import { QuizSkeleton } from "../../ui/skeleton";
import { StateIndicator } from "../../ui/stateIndicator";
import { Quiz, QuizStatus } from "../../models";
import { QuizStore } from "../../store";

type Props = {
  quiz: Quiz | undefined;
};

type Injection = {
  quizStore: QuizStore;
};

@tag("nw-quiz-status-view")
export class NWQuizStatusView extends Component<Props> {
  static css = [tailwind];

  inject = ["quizStore"];

  private handleStartQuiz = () => {
    const isLoggedIn = Store.user.isLoggedIn.value;

    // If user is not logged in, show login modal
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }

    const { quizStore } = this.injection as Injection;
    const stats = quizStore.getLastAttemptStats();

    if (quizStore.hasReachedQuizLimit()) {
      openProModal("project_quiz");
      return;
    }

    if (stats?.passed) {
      quizStore.generateNewQuiz();
    } else {
      quizStore.startQuiz();
    }

    const projectId = Store.project.id.value;
    const quizId = quizStore.quiz.signal.data.value?.id;

    if (quizId && projectId) {
      import("@/services/analyticsService").then(({ AnalyticsService }) => {
        const analyticsService = new AnalyticsService();
        analyticsService.trackQuizStart(quizId, projectId);
      });
    }
  };

  @bind
  renderStartView(quiz: Quiz) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-white text-2xl font-medium">{quiz.name}</h2>

          <StateIndicator quiz={quiz} />
        </div>

        <p className="text-white text-sm">{quiz.description}</p>
      </div>
    );
  }

  @bind
  renderEndView(quiz: Quiz, status: QuizStatus) {
    const title = status === QuizStatus.BelowPassingScore ? "Almost made it!" : "You've mastered it!";

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-white text-2xl font-medium">{title}</h2>

          <StateIndicator quiz={quiz} />
        </div>

        <p className="text-white text-sm">{quiz.description}</p>
      </div>
    );
  }

  render({ quiz }: Props) {
    const hasQuiz = quiz != null;
    const quizStore = (this.injection as Injection).quizStore;
    const status = quizStore.quizStatus.value;
    const stats = quizStore.getLastAttemptStats();
    const hasReachedQuizLimit = quizStore.hasReachedQuizLimit();
    const showStatusView = status === QuizStatus.BelowPassingScore || status === QuizStatus.Passed;
    const isLoggedIn = Store.user.isLoggedIn.peek();

    return (
      <div className="relative bg-[#0C0E12] rounded-3xl px-8 py-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center">
            <BackgroundLines />
          </div>
        </div>

        <div className="relative z-10 flex flex-col items-center text-center space-y-6 max-w-md mx-auto">
          <NWBadge padding="xs" color="brand">
            Pro
          </NWBadge>

          {!isLoggedIn || (hasReachedQuizLimit && !showStatusView) ? (
            <div className="pointer-events-none space-y-6">
              <div className="space-y-2">
                <h2 className="text-white text-2xl font-medium">Project Quiz!</h2>
                <div className="filter blur-sm flex flex-col sm:flex-row justify-center gap-6 mb-6 px-4 py-2 rounded-lg w-fit mx-auto">
                  <div className="flex items-center text-secondary text-sm space-x-1 opacity-100">
                    <div className="w-5 h-5 bg-gray-700/40 rounded-full"></div>
                    <div className="w-20 h-4 bg-gray-700/40 rounded-md"></div>
                  </div>

                  <div className="flex items-center text-secondary text-sm space-x-1 opacity-100">
                    <div className="w-5 h-5 bg-gray-700/40 rounded-full"></div>
                    <div className="w-16 h-4 bg-gray-700/40 rounded-md"></div>
                  </div>
                  <div className="flex items-center text-secondary text-sm space-x-1 opacity-100">
                    <div className="w-5 h-5 bg-gray-700/40 rounded-full"></div>
                    <div className="w-32 h-4 bg-gray-700/40 rounded-md"></div>
                  </div>
                </div>
              </div>
              <p className="text-white text-sm">
                Research shows that testing yourself is one of the best ways to retain what you learn! Upgrade now to
                unlock more quizzes and boost your learning experience.
              </p>
            </div>
          ) : (
            <div>
              {!hasQuiz && <QuizSkeleton />}
              {hasQuiz && (status === QuizStatus.NotStarted || status == null) && this.renderStartView(quiz)}
              {hasQuiz && showStatusView && this.renderEndView(quiz, status)}
            </div>
          )}

          <NWButton
            type="secondary-color"
            size="full"
            class="w-full max-w-80 pt-2 !mb-0"
            loading={isLoggedIn && !hasReachedQuizLimit && (!hasQuiz || quizStore.quiz.signal.isLoading.value)}
            onClick={this.handleStartQuiz}
            subtext={
              !isLoggedIn
                ? "Login to start taking quizzes"
                : hasReachedQuizLimit
                  ? "Maintain your momentum with unlimited quizzes"
                  : undefined
            }
            subtextColor="text-brand-400"
          >
            <span className="text-sm font-semibold">
              {!isLoggedIn
                ? "Login to continue"
                : hasReachedQuizLimit
                  ? "Upgrade to Pro"
                  : stats?.passed
                    ? "Generate a new quiz"
                    : stats != null
                      ? "Retake the quiz"
                      : "Take the quiz"}
            </span>
          </NWButton>
        </div>
      </div>
    );
  }
}
